import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Wallet, Award, Users, ArrowUpRight, Plus, Clock, AlertTriangle, Ban, PauseCircle } from 'lucide-react';
import { accountsAPI, payoutsAPI, affiliateAPI } from '@/lib/api';
import { formatInt, formatMoney, prettyPhase, prettyPlan, nextRequestForAccount } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [aff, setAff] = useState(null);

  useEffect(() => {
    accountsAPI.mine().then(setAccounts).catch(() => {});
    payoutsAPI.mine().then(setPayouts).catch(() => {});
    affiliateAPI.me().then(setAff).catch(() => {});
  }, []);

  const isTerminated = !!user?.is_terminated;

  // Account-size totals (exclude breached accounts)
  const activeAccounts = accounts.filter(a => a.status !== 'breached');
  const totalSize = activeAccounts.reduce((t, a) => t + Number(a.account_size || 0), 0);
  const activeCount = activeAccounts.length;
  const funded = accounts.filter(a => a.phase === 'funded' && a.status === 'active').length;
  // Aggregate manual PnL across non-breached accounts. Falls back to 0 if none set.
  const totalPnl = activeAccounts.reduce((t, a) => t + Number(a.manual_pnl_usd ?? a.metrics?.pnl ?? 0), 0);
  const breached = accounts.filter(a => a.status === 'breached').length;

  return (
    <div className="space-y-6">
      {isTerminated && <TerminationBanner reason={user?.termination_reason} />}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-sm mb-2">Trading hub</div>
          <h1 className="font-display text-3xl sm:text-4xl">Welcome back, {user?.name || 'Trader'}.</h1>
        </div>
        {isTerminated ? (
          <span className="chip chip-red font-bold" data-testid="dash-terminated-pill">
            <Ban size={12} className="inline mr-1" /> ACCOUNT TERMINATED
          </span>
        ) : (
          <Link to="/plans" className="btn-primary btn-sm" data-testid="dash-new-challenge"><Plus size={14} /> Start Evaluation</Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Simulated Size" value={`$${formatInt(totalSize)}`} icon={Wallet} testId="stat-size" />
        <PnlStatCard pnl={totalPnl} />
        <StatCard label="Performance Accounts" value={`${funded}`} icon={Award} testId="stat-funded" />
        <StatCard label="Affiliate Earned" value={`$${formatMoney(aff?.affiliate_total_earned || 0)}`} icon={Users} testId="stat-affiliate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-sm">Performance analytics</div>
              <div className="font-display text-2xl mt-1">Coming soon</div>
              <div className="text-xs text-zinc-500 mt-1">Live equity, P/L and drawdown curves will appear here once our MT5 data bridge goes live.</div>
            </div>
            <div className="chip chip-green"><span className="pulse-dot" /> COMING SOON</div>
          </div>
          <div className="relative mt-6 h-60 border border-white/5 rounded-lg overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-black flex items-center justify-center" data-testid="dash-coming-soon">
            <svg viewBox="0 0 800 120" className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="none">
              <polyline fill="none" stroke="#00ff66" strokeWidth="1"
                points="0,90 60,80 120,70 180,72 240,60 300,58 360,48 420,50 480,40 540,32 600,28 660,34 720,22 780,18 800,16"/>
            </svg>
            <div className="relative text-center">
              <div className="font-display text-3xl">Live Equity Curve</div>
              <div className="text-sm text-zinc-500 mt-2 max-w-sm">Stay tuned — once the MT5 bridge is active, you'll see per-account PnL, drawdown and performance charts here.</div>
            </div>
          </div>
        </div>

        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Recent reward requests</div>
          <div className="mt-4 space-y-3">
            {payouts.length === 0 && <div className="text-sm text-zinc-500">No reward requests yet.</div>}
            {payouts.slice(0, 5).map(p => (
              <div key={p.payout_id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0" data-testid={`recent-payout-${p.payout_id}`}>
                <div>
                  <div className="font-mono text-sm">${formatMoney(p.amount)}</div>
                  <div className="label-xs mt-0.5">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`chip ${p.status === 'pending' ? 'chip-yellow' : p.status === 'approved' || p.status === 'paid' ? 'chip-green' : 'chip-red'}`}>{p.status.toUpperCase()}</span>
              </div>
            ))}
          </div>
          <Link to="/payouts" className="btn-ghost btn-sm mt-4 w-full text-center block" data-testid="view-all-payouts">View all rewards</Link>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">Your accounts</h2>
          <Link to="/accounts" className="text-sm text-zinc-400 hover:text-white" data-testid="view-all-accounts">View all →</Link>
        </div>
        {accounts.length === 0 ? (
          <div className="card-tech rounded-lg p-12 text-center">
            <div className="label-sm mb-2">No accounts yet</div>
            <h3 className="font-display text-2xl">Start your first evaluation</h3>
            <p className="text-zinc-500 text-sm mt-1">Choose a program, pick your size, and start trading in minutes.</p>
            <Link to="/plans" className="btn-primary mt-6 inline-flex">Browse programs <ArrowUpRight size={14} /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map(a => <AccountCard key={a.account_id} a={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-tech rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="label-xs">{label}</div>
        <Icon size={14} className="text-[#00ff66]" />
      </div>
      <div className="font-mono text-2xl mt-2">{value}</div>
    </motion.div>
  );
}

function PnlStatCard({ pnl }) {
  const positive = pnl >= 0;
  const sign = positive ? '+' : '';
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-tech rounded-lg p-5" data-testid="stat-pnl">
      <div className="flex items-center justify-between">
        <div className="label-xs">Total PnL</div>
        <Award size={14} className={positive ? 'text-[#00ff66]' : 'text-[#ff3366]'} />
      </div>
      <div className={`font-mono text-2xl mt-2 ${positive ? 'text-[#00ff66]' : 'text-[#ff3366]'}`}>
        {sign}${formatMoney(Math.abs(pnl))}
      </div>
    </motion.div>
  );
}

function AccountCard({ a }) {
  const next = nextRequestForAccount(a);
  const isBreached = a.status === 'breached';
  const isTerminated = !!a.is_terminated;
  const isPaused = !!a.is_paused && !isTerminated;
  const hasPending = !!a.pending_request;
  const isDim = isBreached || isTerminated || isPaused;

  // Only shows for accounts mid-evaluation with a real profit target (phase_1 /
  // phase_2). Funded accounts and breached/paused/terminated ones skip it —
  // there's nothing to climb toward, or the account isn't actively trading.
  const showProgress = !isDim && a.progress?.target_pct;
  const progressPct = showProgress ? Math.min(100, Math.max(0, a.progress.progress_pct)) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`card-tech rounded-lg p-5 relative ${isDim ? 'opacity-60' : ''} ${isTerminated ? 'border-[#ff3366]/50' : isBreached ? 'border-[#ff3366]/30' : isPaused ? 'border-amber-500/40' : ''}`}
      data-testid={`account-card-${a.account_id}`}>
      {isTerminated && (
        <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-[#ff3366] uppercase tracking-widest" data-testid={`terminated-tag-${a.account_id}`}>
          <Ban size={11} /> Terminated
        </div>
      )}
      {!isTerminated && isPaused && (
        <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-amber-400 uppercase tracking-widest" data-testid={`paused-tag-${a.account_id}`}>
          <PauseCircle size={11} /> Paused
        </div>
      )}
      {!isTerminated && !isPaused && isBreached && (
        <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-[#ff3366] uppercase tracking-widest">
          <AlertTriangle size={11} /> Breached
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <div className="label-xs">#{a.mt_login || 'PENDING'} · {prettyPlan(a.plan_id)}</div>
          <div className="font-display text-xl mt-1">${formatInt(a.account_size)}</div>
        </div>
        <span className={`chip ${isTerminated ? 'chip-red' : isPaused ? 'chip-yellow' : isBreached ? 'chip-red' : a.phase === 'funded' ? 'chip-green' : 'chip-yellow'}`}>
          {isTerminated ? 'TERMINATED' : isPaused ? 'PAUSED' : prettyPhase(a.phase)}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="label-xs">PROGRAM</div>
          <div className="text-sm truncate">{a.plan_name}</div>
        </div>
        <div>
          <div className="label-xs">STATUS</div>
          <div className="text-sm">{isTerminated ? 'Terminated' : isPaused ? 'Paused' : isBreached ? 'Breached' : hasPending ? 'Pending' : 'Active'}</div>
        </div>
        {(() => {
          const pnl = Number(a.manual_pnl_usd ?? a.metrics?.pnl ?? 0);
          const pos = pnl >= 0;
          return (
            <div>
              <div className="label-xs">PNL</div>
              <div className={`text-sm font-mono ${pos ? 'text-[#00ff66]' : 'text-[#ff3366]'}`}>
                {pos ? '+' : ''}${formatMoney(Math.abs(pnl))}
              </div>
            </div>
          );
        })()}
      </div>

      {showProgress && (
        <div className="mt-4" data-testid={`progress-${a.account_id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="label-xs">Progress to target</span>
            <span className="text-xs font-mono text-zinc-400">{Math.round(progressPct)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#00ff66]/60 to-[#00ff66]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-4">
        {isTerminated ? (
          <div className="text-xs text-zinc-500">Account permanently disabled by compliance.</div>
        ) : isPaused ? (
          <div className="text-xs text-amber-400/90 leading-relaxed" data-testid={`paused-note-${a.account_id}`}>
            Paused while the new management team completes the account migration.
          </div>
        ) : hasPending && !isBreached ? (
          <div className="chip chip-yellow" data-testid={`dash-acc-pending-${a.account_id}`}>
            <Clock size={10} className="inline mr-1" /> Pending admin review
          </div>
        ) : isBreached ? (
          <Link to="/plans" className="btn-ghost btn-sm inline-flex" data-testid={`rebuy-${a.account_id}`}>Purchase new program →</Link>
        ) : next ? (
          <Link to={`/accounts/${a.account_id}`} className="btn-ghost btn-sm inline-flex" data-testid={`open-account-${a.account_id}`}>
            {next.label} →
          </Link>
        ) : (
          <Link to={`/accounts/${a.account_id}`} className="btn-ghost btn-sm inline-flex" data-testid={`open-account-${a.account_id}`}>
            Open account →
          </Link>
        )}
      </div>
    </motion.div>
  );
}

function TerminationBanner({ reason }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-lg border border-[#ff3366]/40"
      data-testid="dash-terminated-banner"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,51,102,0.18),transparent_60%)]" />
      <div className="relative flex items-start gap-4 p-5 sm:p-6 bg-black/60">
        <div className="w-11 h-11 rounded-full bg-[#ff3366]/15 border border-[#ff3366]/40 flex items-center justify-center shrink-0">
          <Ban className="text-[#ff3366]" size={22} />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[3px] text-[#ff3366]">RISK &amp; COMPLIANCE</div>
          <div className="font-display text-3xl sm:text-4xl mt-1 text-[#ff3366] leading-none">BANNED</div>
          <p className="text-sm text-zinc-300 mt-3 max-w-2xl">
            Your Traders Spring Group account has been permanently terminated by our Risk &amp; Compliance team.
            You retain read-only access to your dashboard, but you cannot purchase new evaluations, request
            phase upgrades, submit payout requests, or open affiliate withdrawals. This decision is final.
          </p>
          {reason && reason.trim() && (
            <div className="mt-3 text-xs text-zinc-400 border-l-2 border-[#ff3366]/50 pl-3">
              Reason on file: <span className="text-zinc-200">{reason}</span>
            </div>
          )}
          <div className="mt-3 text-xs text-zinc-500">
            For questions, contact <a href="mailto:support@tradersspringfunding.com" className="text-[#ff3366] underline">support@tradersspringfunding.com</a>.
          </div>
        </div>
      </div>
    </motion.div>
  );
}